import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Client, OrdersRequest } from '../../../api/client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/authentication/auth.service.service';
import { fromEvent, timer, EMPTY } from 'rxjs';
import { switchMap, map, startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserCountInfoService {
  private client = inject(Client);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthService);

  private readonly TWO_HOURS_MS = 0.001 * 60 * 60 * 1000;
  private lastHiddenAt: number | null = null;

  public userBalanceInfo = signal<string | null>(null);
  public userOrdersInfo = signal<number | null>(null);
  public userChatInfo = signal<number | null>(null);

  private orderReq: OrdersRequest = {
    orderNumber: 0,
    createDateFrom: null,
    createDateTo: null,
    actualStatus: null,
    paginator: {
      countStart: 0,
      countFinish: 0,
      countTotal: 0,
      take: 1,
      page: 1,
      pageCount: 1,
    }
  };

  constructor() {
    const profile = this.auth.profile();

    if (profile) {
      this.initSmartPolling();
    } else {
      this.clearSignals();
    }
  }

  private initSmartPolling(): void {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => !document.hidden),
      startWith(!document.hidden)
    );

    visibility$.pipe(
      switchMap(isVisible => {
        if (!isVisible) {
          this.lastHiddenAt = Date.now();
          return EMPTY;
        }

        let delayBeforeFirstRequest = 0;

        if (this.lastHiddenAt) {
          const idleTime = Date.now() - this.lastHiddenAt;

          if (idleTime < this.TWO_HOURS_MS) {
            delayBeforeFirstRequest = this.TWO_HOURS_MS;
          }
        }
        this.lastHiddenAt = null;

        return timer(delayBeforeFirstRequest, this.TWO_HOURS_MS);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.auth.isAuth() ?
      this.initAll() : null;
    });
  }

  public initAll() {
    if (!this.auth.isAuth()) return;
    this.initUserOrdersCount();
    this.initUserBalance();
    const profile = this.auth.profile();
    if (profile?.id) {
      this.initUserMessage(profile.id);
    }
  }

  private clearSignals(): void {
    this.userBalanceInfo.set(null);
    this.userOrdersInfo.set(null);
    this.userChatInfo.set(null);
  }

  public initUserMessage(userId: string): void {
    this.client.adminChat_UserChatInfo({
      take: 10,
      skip: 0,
      userId: userId
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (data) => {
          const unreadCount = data.messages.filter(m => !m.isRead).length;
          this.userChatInfo.set(unreadCount);
        },
        error: err => console.error(err.message)
      });
  }

  public initUserBalance(): void {
    this.client.profile_UserBalance()
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => this.userBalanceInfo.set(`${result.amount} ${result.symbol}`),
        error: err => console.error(err.message)
      });
  }

  public initUserOrdersCount(): void {
    this.client.order_PaggingUserOrders(this.orderReq)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => this.userOrdersInfo.set(result.activeOrdersCount),
        error: err => console.error(err.message)
      });
  }
}
